import { useState } from 'react';
import { useDispatch } from 'react-redux';
import Switch from '@mui/material/Switch';

// @mui
import {
  Alert,
  Box,
  Chip,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

// components
import api from 'src/api';
import { loadingStartAction, loadingStopAction } from 'src/redux/actions/loadingActions';

// ----------------------------------------------------------------------

export default function AppSettings({ data, getAllSettings }) {
  const [checked, setChecked] = useState(data?.appSettings?.under_maintaince);
  // Settings documents that predate this field have no value stored, and the
  // server treats anything but an explicit false as enabled, so the switch has
  // to read the same way round.
  const [razorpayOn, setRazorpayOn] = useState(data?.appSettings?.razorpay_enabled !== false);
  // Auto declaration is the opposite: off unless explicitly turned on.
  const [autoResultOn, setAutoResultOn] = useState(data?.appSettings?.auto_result_enabled === true);

  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [checking, setChecking] = useState(false);

  const dispatch = useDispatch();

  const handleChange = async (event) => {
    setChecked(event.target.checked);
    dispatch(loadingStartAction());

    const reqBody = {
      under_maintaince: event.target.checked,
    };
    await api.put('/settings', reqBody);
    dispatch(loadingStopAction());
    getAllSettings();
  };

  const handleRazorpayChange = async (event) => {
    setRazorpayOn(event.target.checked);
    dispatch(loadingStartAction());

    await api.put('/settings', { razorpay_enabled: event.target.checked });
    dispatch(loadingStopAction());
    getAllSettings();
  };

  const handleAutoResultChange = async (event) => {
    setAutoResultOn(event.target.checked);
    dispatch(loadingStartAction());

    await api.put('/settings', { auto_result_enabled: event.target.checked });
    dispatch(loadingStopAction());
    getAllSettings();
  };

  /**
   * Reads the source and shows what it would work from. This only reads: it
   * declares nothing and moves no money, so it is the safe way to find out
   * whether the server can reach the source and whether the market names line
   * up before the switch above is ever turned on.
   */
  const checkSource = async () => {
    setChecking(true);
    setPreviewError('');
    setPreview(null);

    try {
      const res = await api.get('/auto-results/preview');
      setPreview(res.data);
    } catch (error) {
      setPreviewError(
        error?.response?.data?.message || error?.message || 'Could not read the source'
      );
    } finally {
      setChecking(false);
    }
  };

  const rows = preview?.rows || [];
  const mappedRows = rows.filter((r) => r.mapped_game);

  return (
    <Stack spacing={3}>
      <FormGroup>
        <FormControlLabel
          control={<Switch checked={checked} onChange={handleChange} inputProps={{ 'aria-label': 'controlled' }} />}
          label="Under Maintance"
        />
        <FormControlLabel
          control={
            <Switch checked={razorpayOn} onChange={handleRazorpayChange} inputProps={{ 'aria-label': 'controlled' }} />
          }
          label="Online payments (Razorpay)"
        />
      </FormGroup>

      <Divider />

      <Box>
        <Typography variant="h6" gutterBottom>
          Automatic result declaration
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Reads declared results from the public results site every two minutes. A result is only
          declared after the same numbers have been seen three times over at least four minutes, the
          market&apos;s own session time has passed, and the pana, ank and jodi all agree with each
          other. If the source ever changes its answer, that market is held back for you to decide.
        </Typography>

        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={autoResultOn}
                onChange={handleAutoResultChange}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            }
            label={autoResultOn ? 'On — results declare themselves' : 'Off — declare results by hand'}
          />
        </FormGroup>

        {autoResultOn && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Declaring pays winners immediately and cannot be undone. Use <b>Check source</b> below
            and confirm the markets map to the right games before leaving this on.
          </Alert>
        )}

        <LoadingButton
          variant="outlined"
          loading={checking}
          onClick={checkSource}
          sx={{ mt: 2 }}
        >
          Check source
        </LoadingButton>

        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
          Reads the source and shows what it says. Declares nothing.
        </Typography>

        {previewError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {previewError}
          </Alert>
        )}

        {preview && (
          <Box sx={{ mt: 2 }}>
            <Alert severity={mappedRows.length ? 'success' : 'warning'} sx={{ mb: 2 }}>
              Reached <b>{preview.source}</b> — {rows.length} markets found, {mappedRows.length}{' '}
              matched to your games.
              {mappedRows.length === 0 && ' Nothing is mapped, so nothing would be declared.'}
            </Alert>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Market on source</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell>Your game</TableCell>
                    <TableCell>Numbers agree</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={`${row.market}-${row.raw}`}>
                      <TableCell>{row.market}</TableCell>
                      <TableCell>{row.raw}</TableCell>
                      <TableCell>
                        {row.mapped_game ? (
                          <Chip
                            size="small"
                            color={row.auto_enabled ? 'success' : 'default'}
                            label={row.auto_enabled ? row.mapped_game : `${row.mapped_game} (off)`}
                          />
                        ) : (
                          <Chip size="small" variant="outlined" label="not mapped" />
                        )}
                      </TableCell>
                      <TableCell>
                        {row.valid ? (
                          <Chip size="small" color="success" label="yes" />
                        ) : (
                          <Chip size="small" color="error" label={row.problems?.[0] || 'no'} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>
    </Stack>
  );
}
